// src/Components/Modules/HR/AssetManager.js
import React, { useState, useEffect, useMemo } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  Stack,
  Avatar,
  TextField,
  InputAdornment,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import {
  FaPlus,
  FaLaptop,
  FaUserPlus,
  FaTrash,
  FaBoxOpen,
  FaCheckCircle,
  FaTimesCircle,
  FaMobileAlt,
  FaDesktop,
  FaSearch,
  FaSync,
  FaFilter,
  FaBarcode,
  FaUser,
  FaKeyboard,
  FaHeadphones,
  FaChair,
  FaClipboardList,
  FaExchangeAlt,
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

// Styled Components
const GradientPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
}));

const StatCard = styled(Card)(({ theme }) => ({
  background: "white",
  borderRadius: theme.spacing(3),
  padding: theme.spacing(3),
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.3s ease",
  border: "1px solid rgba(0,0,0,0.08)",
  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  transition: "background-color 0.2s ease",
}));

const ActionButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 600,
  padding: theme.spacing(0.6, 1.5),
  minWidth: "auto",
  ...(variant === "assign" && {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    "&:hover": {
      background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
    },
  }),
  ...(variant === "unassign" && {
    background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    color: "#b71c1c",
    "&:hover": {
      background: "linear-gradient(135deg, #fee140 0%, #fa709a 100%)",
      boxShadow: "0 4px 12px rgba(250, 112, 154, 0.3)",
    },
  }),
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 600,
  padding: theme.spacing(1.2, 3),
  background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
  color: "#1b5e20",
  "&:hover": {
    background: "linear-gradient(135deg, #38f9d7 0%, #43e97b 100%)",
    boxShadow: "0 6px 20px rgba(67, 233, 123, 0.4)",
  },
}));

const AssetManager = () => {
  const [assets, setAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [newAsset, setNewAsset] = useState({
    name: "",
    type: "laptop",
    asset_code: "",
  });
  const [loading, setLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState(null);
  const [assignDialog, setAssignDialog] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);

  const role = Number(localStorage.getItem("who_am_i"));
  const canManage = role <= 2;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [assetsRes, employeesRes] = await Promise.all([
        AxiosAuthInstance.get("hr/assets"),
        AxiosAuthInstance.get("add_employee"),
      ]);
      setAssets(Array.isArray(assetsRes.data) ? assetsRes.data : []);
      setEmployees(
        Array.isArray(employeesRes.data.employee)
          ? employeesRes.data.employee.map((emp) => ({
              label: emp.employee_name,
              value: emp.id,
            }))
          : []
      );
    } catch (err) {
      setError("Failed to load data");
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load asset data. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const create = async () => {
    if (!newAsset.name.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please enter an asset name",
      });
      return;
    }
    if (!newAsset.asset_code.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please enter an asset code",
      });
      return;
    }

    setAddLoading(true);
    try {
      await AxiosAuthInstance.post("hr/assets", newAsset);
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Asset added successfully",
        timer: 2000,
        showConfirmButton: false,
      });
      const res = await AxiosAuthInstance.get("hr/assets");
      setAssets(Array.isArray(res.data) ? res.data : []);
      setNewAsset({ name: "", type: "laptop", asset_code: "" });
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Failed to add asset";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMsg,
      });
    } finally {
      setAddLoading(false);
    }
  };

  const openAssignDialog = (asset) => {
    setAssignDialog(asset);
    setSelectedEmployee("");
  };

  const assign = async () => {
    if (!selectedEmployee) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please select an employee",
      });
      return;
    }

    setAssignLoading(true);
    try {
      await AxiosAuthInstance.post(`hr/assets/${assignDialog.id}/assign`, {
        employee_id: selectedEmployee,
      });
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Asset assigned successfully",
        timer: 2000,
        showConfirmButton: false,
      });
      const res = await AxiosAuthInstance.get("hr/assets");
      setAssets(Array.isArray(res.data) ? res.data : []);
      setAssignDialog(null);
      setSelectedEmployee("");
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Failed to assign asset";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMsg,
      });
    } finally {
      setAssignLoading(false);
    }
  };

  const unassign = async (id) => {
    const result = await Swal.fire({
      title: "Unassign Asset?",
      text: "Are you sure you want to unassign this asset?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#f44336",
      cancelButtonColor: "#757575",
      confirmButtonText: "Yes, Unassign",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await AxiosAuthInstance.post(`hr/assets/${id}/unassign`);
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Asset unassigned successfully",
          timer: 2000,
          showConfirmButton: false,
        });
        const res = await AxiosAuthInstance.get("hr/assets");
        setAssets(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        const errorMsg = err.response?.data?.message || "Failed to unassign";
        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMsg,
        });
      }
    }
  };

  const deleteAsset = async (id) => {
    const result = await Swal.fire({
      title: "Delete Asset?",
      text: "This action cannot be undone. Are you sure?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f44336",
      cancelButtonColor: "#757575",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await AxiosAuthInstance.delete(`hr/assets/${id}`);
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "Asset deleted successfully",
          timer: 2000,
          showConfirmButton: false,
        });
        const res = await AxiosAuthInstance.get("hr/assets");
        setAssets(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        const errorMsg = err.response?.data?.message || "Failed to delete";
        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMsg,
        });
      }
    }
  };

  const filtered = useMemo(() => {
    if (!Array.isArray(assets)) return [];
    return assets.filter((item) => {
      const name = item.name?.toLowerCase() || "";
      const code = item.asset_code?.toLowerCase() || "";
      const type = item.type?.toLowerCase() || "";
      const status = item.status?.toLowerCase() || "";
      const employeeName = item.assigned_to?.employee_name?.toLowerCase() || "";
      const searchTerm = search.toLowerCase();

      const matchesSearch =
        name.includes(searchTerm) ||
        code.includes(searchTerm) ||
        type.includes(searchTerm) ||
        employeeName.includes(searchTerm);

      const matchesType = typeFilter === "all" || type === typeFilter;
      const matchesStatus = statusFilter === "all" || status === statusFilter;

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [assets, search, typeFilter, statusFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Calculate stats
  const stats = useMemo(() => {
    if (!Array.isArray(assets)) {
      return { total: 0, assigned: 0, available: 0, laptop: 0, phone: 0, monitor: 0 };
    }
    return {
      total: assets.length,
      assigned: assets.filter((a) => a.status === "assigned").length,
      available: assets.filter((a) => a.status === "available" || !a.status).length,
      laptop: assets.filter((a) => a.type === "laptop").length,
      phone: assets.filter((a) => a.type === "phone").length,
      monitor: assets.filter((a) => a.type === "monitor").length,
    };
  }, [assets]);

  const getTypeIcon = (type) => {
    const typeLower = type?.toLowerCase();
    switch (typeLower) {
      case "laptop":
        return <FaLaptop />;
      case "phone":
        return <FaMobileAlt />;
      case "monitor":
        return <FaDesktop />;
      case "keyboard":
        return <FaKeyboard />;
      case "headphone":
      case "headset":
        return <FaHeadphones />;
      case "chair":
        return <FaChair />;
      default:
        return <FaBoxOpen />;
    }
  };

  const getTypeColor = (type) => {
    const typeLower = type?.toLowerCase();
    switch (typeLower) {
      case "laptop":
        return { bg: "#e3f2fd", color: "#1976d2" };
      case "phone":
        return { bg: "#f3e5f5", color: "#7b1fa2" };
      case "monitor":
        return { bg: "#e8f5e9", color: "#2e7d32" };
      case "keyboard":
        return { bg: "#fff3e0", color: "#ef6c00" };
      case "headphone":
      case "headset":
        return { bg: "#fce4ec", color: "#c2185b" };
      default:
        return { bg: "#f5f5f5", color: "#757575" };
    }
  };

  const getStatusChip = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === "assigned") {
      return (
        <Chip
          icon={<FaCheckCircle style={{ fontSize: 12 }} />}
          label="Assigned"
          size="small"
          sx={{
            bgcolor: "#e8f5e9",
            color: "#2e7d32",
            fontWeight: 600,
            "& .MuiChip-icon": { color: "#4caf50" },
          }}
        />
      );
    }
    return (
      <Chip
        icon={<FaTimesCircle style={{ fontSize: 12 }} />}
        label="Available"
        size="small"
        sx={{
          bgcolor: "#fff3e0",
          color: "#ef6c00",
          fontWeight: 600,
          "& .MuiChip-icon": { color: "#ff9800" },
        }}
      />
    );
  };

  const getInitials = (name) => {
    if (!name) return "N/A";
    const names = name.split(" ");
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getColorFromName = (name) => {
    const colors = [
      "#667eea", "#764ba2", "#f093fb", "#4facfe",
      "#43e97b", "#fa709a", "#fee140", "#30cfd0",
    ];
    if (!name) return colors[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  if (loading && assets.length === 0) {
    return (
      <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
        <Breadcrumb title="Asset Management" />
        <GradientPaper>
          <Skeleton variant="text" width="60%" height={60} />
          <Skeleton variant="text" width="40%" />
        </GradientPaper>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="Asset Management" />

      {/* Header */}
      <GradientPaper elevation={0}>
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "center", sm: "space-between" },
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "center", sm: "flex-start" },
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            <Typography variant="h4" fontWeight={700} gutterBottom>
              <FaBoxOpen style={{ marginRight: 10, verticalAlign: "middle" }} />
              Asset Management
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Track and manage company assets efficiently
            </Typography>
          </Box>
          <Tooltip title="Refresh Data">
            <IconButton
              onClick={loadData}
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "white",
                "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
              }}
            >
              <FaSync />
            </IconButton>
          </Tooltip>
        </Box>
      </GradientPaper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={0}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                bgcolor: "#e3f2fd",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <FaBoxOpen size={28} color="#2196f3" />
            </Box>
            <Typography variant="h3" fontWeight={700} color="#1565c0" sx={{ mb: 0.5 }}>
              {stats.total}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Total Assets
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={0}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                bgcolor: "#e8f5e9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <FaCheckCircle size={28} color="#4caf50" />
            </Box>
            <Typography variant="h3" fontWeight={700} color="#2e7d32" sx={{ mb: 0.5 }}>
              {stats.assigned}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Assigned
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={0}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                bgcolor: "#fff3e0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <FaTimesCircle size={28} color="#ff9800" />
            </Box>
            <Typography variant="h3" fontWeight={700} color="#ef6c00" sx={{ mb: 0.5 }}>
              {stats.available}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Available
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={0}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                bgcolor: "#f3e5f5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <FaLaptop size={28} color="#7b1fa2" />
            </Box>
            <Typography variant="h3" fontWeight={700} color="#7b1fa2" sx={{ mb: 0.5 }}>
              {stats.laptop}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Laptops
            </Typography>
          </StatCard>
        </Grid>
      </Grid>

      {/* Add New Asset Form */}
      {canManage && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            <FaPlus style={{ marginRight: 8, verticalAlign: "middle" }} />
            Add New Asset
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Asset Name"
                placeholder="e.g., MacBook Pro"
                value={newAsset.name}
                onChange={(e) => setNewAsset({ ...newAsset, name: e.target.value })}
                variant="outlined"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Asset Type"
                value={newAsset.type}
                onChange={(e) => setNewAsset({ ...newAsset, type: e.target.value })}
                variant="outlined"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              >
                <MenuItem value="laptop">
                  <FaLaptop style={{ marginRight: 8 }} /> Laptop
                </MenuItem>
                <MenuItem value="phone">
                  <FaMobileAlt style={{ marginRight: 8 }} /> Phone
                </MenuItem>
                <MenuItem value="monitor">
                  <FaDesktop style={{ marginRight: 8 }} /> Monitor
                </MenuItem>
                <MenuItem value="keyboard">
                  <FaKeyboard style={{ marginRight: 8 }} /> Keyboard
                </MenuItem>
                <MenuItem value="headphone">
                  <FaHeadphones style={{ marginRight: 8 }} /> Headphone
                </MenuItem>
                <MenuItem value="other">
                  <FaBoxOpen style={{ marginRight: 8 }} /> Other
                </MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Asset Code"
                placeholder="e.g., AST-001"
                value={newAsset.asset_code}
                onChange={(e) => setNewAsset({ ...newAsset, asset_code: e.target.value })}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaBarcode style={{ color: "#757575" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <PrimaryButton
                fullWidth
                variant="contained"
                onClick={create}
                disabled={addLoading}
                startIcon={addLoading ? null : <FaPlus />}
                sx={{ height: 40 }}
              >
                {addLoading ? "Adding..." : "Add Asset"}
              </PrimaryButton>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <TextField
            placeholder="Search by name, code, employee..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaSearch style={{ color: "#757575" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.default",
              },
            }}
          />
          <TextField
            select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              minWidth: 150,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.default",
              },
            }}
          >
            <MenuItem value="all">All Types</MenuItem>
            <MenuItem value="laptop">Laptop</MenuItem>
            <MenuItem value="phone">Phone</MenuItem>
            <MenuItem value="monitor">Monitor</MenuItem>
            <MenuItem value="keyboard">Keyboard</MenuItem>
            <MenuItem value="headphone">Headphone</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
          <TextField
            select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              minWidth: 150,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.default",
              },
            }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="available">Available</MenuItem>
            <MenuItem value="assigned">Assigned</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#fafafa" }}>
              <StyledTableCell width={80}>SL</StyledTableCell>
              <StyledTableCell>Asset</StyledTableCell>
              <StyledTableCell>Code</StyledTableCell>
              <StyledTableCell>Type</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Assigned To</StyledTableCell>
              {canManage && <StyledTableCell align="center">Actions</StyledTableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from(new Array(5)).map((_, index) => (
                <TableRow key={index}>
                  <TableCell><Skeleton width={40} /></TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Skeleton variant="circular" width={40} height={40} />
                      <Skeleton width={120} />
                    </Box>
                  </TableCell>
                  <TableCell><Skeleton width={100} /></TableCell>
                  <TableCell><Skeleton width={80} /></TableCell>
                  <TableCell><Skeleton width={80} /></TableCell>
                  <TableCell><Skeleton width={120} /></TableCell>
                  {canManage && <TableCell><Skeleton width={150} /></TableCell>}
                </TableRow>
              ))
            ) : paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canManage ? 7 : 6} align="center" sx={{ py: 8 }}>
                  <Box sx={{ textAlign: "center" }}>
                    <FaBoxOpen style={{ fontSize: 48, color: "#bdbdbd", marginBottom: 16 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No assets found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {search || typeFilter !== "all" || statusFilter !== "all"
                        ? "Try adjusting your filters"
                        : "Add your first asset to get started"}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, index) => {
                const typeColor = getTypeColor(row.type);
                const isAssigned = row.status?.toLowerCase() === "assigned";

                return (
                  <StyledTableRow key={row.id || index} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar
                          sx={{
                            bgcolor: typeColor.bg,
                            color: typeColor.color,
                            width: 40,
                            height: 40,
                          }}
                        >
                          {getTypeIcon(row.type)}
                        </Avatar>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {row.name || "N/A"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.asset_code || "N/A"}
                        size="small"
                        icon={<FaBarcode style={{ fontSize: 12 }} />}
                        sx={{
                          bgcolor: "#f5f5f5",
                          color: "#424242",
                          fontWeight: 600,
                          fontFamily: "monospace",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.type || "N/A"}
                        size="small"
                        icon={getTypeIcon(row.type)}
                        sx={{
                          bgcolor: typeColor.bg,
                          color: typeColor.color,
                          fontWeight: 600,
                          textTransform: "capitalize",
                        }}
                      />
                    </TableCell>
                    <TableCell>{getStatusChip(row.status)}</TableCell>
                    <TableCell>
                      {row.assigned_to ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Avatar
                            sx={{
                              bgcolor: getColorFromName(row.assigned_to.employee_name),
                              width: 32,
                              height: 32,
                              fontSize: "0.875rem",
                              fontWeight: 600,
                            }}
                          >
                            {getInitials(row.assigned_to.employee_name)}
                          </Avatar>
                          <Typography variant="body2" fontWeight={500}>
                            {row.assigned_to.employee_name}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                          Not assigned
                        </Typography>
                      )}
                    </TableCell>
                    {canManage && (
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          {isAssigned ? (
                            <Tooltip title="Unassign Asset">
                              <span>
                                <ActionButton
                                  variant="unassign"
                                  size="small"
                                  onClick={() => unassign(row.id)}
                                  startIcon={<FaExchangeAlt />}
                                >
                                  Unassign
                                </ActionButton>
                              </span>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Assign to Employee">
                              <span>
                                <ActionButton
                                  variant="assign"
                                  size="small"
                                  onClick={() => openAssignDialog(row)}
                                  startIcon={<FaUserPlus />}
                                >
                                  Assign
                                </ActionButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete Asset">
                            <IconButton
                              size="small"
                              onClick={() => deleteAsset(row.id)}
                              sx={{ color: "#f44336" }}
                            >
                              <FaTrash fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    )}
                  </StyledTableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {!loading && filtered.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filtered.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: 1,
              borderColor: "divider",
              "& .MuiTablePagination-toolbar": {
                paddingRight: 2,
              },
            }}
          />
        )}
      </StyledTableContainer>

      {/* Assign Dialog */}
      <Dialog
        open={!!assignDialog}
        onClose={() => !assignLoading && setAssignDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: "#667eea", color: "white" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FaUserPlus />
            Assign Asset
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {assignDialog && (
            <Stack spacing={3}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#f5f5f5",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: getTypeColor(assignDialog.type).bg,
                    color: getTypeColor(assignDialog.type).color,
                    width: 50,
                    height: 50,
                  }}
                >
                  {getTypeIcon(assignDialog.type)}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {assignDialog.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Code: {assignDialog.asset_code} • Type: {assignDialog.type}
                  </Typography>
                </Box>
              </Box>

              <TextField
                select
                fullWidth
                label="Select Employee"
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              >
                {employees.map((emp) => (
                  <MenuItem key={emp.value} value={emp.value}>
                    {emp.label}
                  </MenuItem>
                ))}
              </TextField>

              <Alert severity="info" icon={<FaClipboardList />}>
                This asset will be assigned to the selected employee. They will be
                responsible for its care and maintenance.
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setAssignDialog(null)}
            disabled={assignLoading}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={assign}
            disabled={assignLoading}
            startIcon={assignLoading ? null : <FaUserPlus />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              },
            }}
          >
            {assignLoading ? "Assigning..." : "Assign Asset"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssetManager;